'use client';

import { AlertCircle } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRepScoreStore } from '@/presentation/hooks/useStores';
import { AT_RISK_CONFIG } from '../constants';
import { ScoreCard, StatusTile, DrawerWhy, DrawerCTA } from '../shared';

interface AtRiskStateProps {
  onPayNow?: () => void;
}

const WHY_COPY =
  'Paying on time keeps your trust level steady. A late payment can hold back Early Pickup on future plans.';

/**
 * STATE 4: At Risk (late payment within the grace period)
 *
 * Linter-yellow notice banner up top with the specific missed date when
 * available. Score card stays intact since the trust level has not yet
 * dropped. Primary CTA routes to the payment flow.
 */
export const AtRiskState = observer(function AtRiskState({
  onPayNow,
}: AtRiskStateProps) {
  const repScoreStore = useRepScoreStore();
  const score = repScoreStore.currentScore;
  const tier = repScoreStore.trustTier;
  const tierConfig = repScoreStore.tierConfig;
  const recentLatePayment = repScoreStore.recentScoreChanges.find((change) =>
    change.trigger?.toLowerCase().includes('late')
  );
  const missDateLabel = recentLatePayment?.date
    ? new Date(recentLatePayment.date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
      })
    : null;

  const banner = missDateLabel
    ? `You missed a payment on ${missDateLabel}. Pay now within ${AT_RISK_CONFIG.gracePeriodHours} hours to keep your ${tierConfig.displayName} trust level.`
    : `Pay now within ${AT_RISK_CONFIG.gracePeriodHours} hours to keep your ${tierConfig.displayName} trust level.`;

  return (
    <div className="flex flex-col gap-[18px]">
      <StatusTile
        variant="warning"
        icon={<AlertCircle className="size-4 text-white" />}
        title="A payment was missed"
        body={banner}
      />

      <ScoreCard
        score={score}
        tier={tier}
        helper="Your trust level holds while the payment is still within the grace period."
      />

      <DrawerWhy body={WHY_COPY} />

      <DrawerCTA label="Make payment" onClick={onPayNow} variant="blue" />
    </div>
  );
});

export default AtRiskState;
